/**
 * The confirm step's raw sample (TMMAPP-0120 item 12): the file's first rows as the bank wrote
 * them, in a scrollable region of its own (the `TableCard` pattern: focusable and named "Raw
 * sample"), so it never paints under the mapping form beside it.
 *
 * - TMMAPP-0139 item 4: the prototype's 12px mono (`monoSmall`, P:1746) for cells and row numbers.
 *   The region scrolls both ways at most 60 % of the viewport tall, so its sideways scrollbar is
 *   always on screen; the "rows above the header" control sits above it and the header row is
 *   sticky at its top.
 * - The columns mapped to an amount, a debit, a credit or the running balance are right-aligned,
 *   with tabular numerals, and never wrap.
 * - A text cell is one line: it ends in an ellipsis at its widest (Description, the payee column,
 *   wider than the others). One that can be cut off is focusable and carries its full text in a
 *   tooltip on hover and focus; a screen reader reads the whole text either way.
 * - The file as it is (TMMAPP-0123 item 3, TMMAPP-0131): every row carries its **file row number**.
 *   `preambleSample` (numbered from `preambleFrom`) sits above `rows`, which start at the detected
 *   header. Those numbers stay put when Header row / First data row change — the tints follow the fields.
 *   Colour and background sit on every cell (the row-number cell included). `MuiTableCell` body
 *   ink and the head slot (`text2` on `surface2`) beat a row, so the cell uses `&&`. The header
 *   is `infoText` on `infoSoft` at weight 500 ("Header"); the first data row is `successText` on
 *   `successSoft` ("First data row"); later rows have no tint ("Data"). Rows above the header are
 *   `text2` ("Above header"). The fold counts file rows above the header (`headerRow - 1`). The
 *   expand counts the served rows above the header. When `preambleRows` is larger than the served
 *   preamble, the line reads "Showing the last {shown} of {n} rows above the header". A preamble
 *   that was not served at all stays "{n} rows above the header (not shown)".
 *   When Header row is not one of the rows held here, a quiet line says so and no row is
 *   tinted "Header" (TMMAPP-0130 item 9a).
 * - Pick from the sample (TMMAPP-0136 item 4b): with the pick handlers, each held row's number is a
 *   button ("Row {n}") whose menu sets Header row or First data row; the tints move at once. Only
 *   held rows are listed, so a row above the served window can't be picked.
 */
import { useId, useState, type MouseEvent } from 'react';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { ColumnMap } from '@/api/imports';
import { resolveTokens } from '@/theme/theme';
import { sampleFileRow } from './mapping';

/**
 * A text cell's widest, in characters: `ch` is exact in a mono face, so a cell is cut off exactly
 * when its text is longer. Description (the payee column) is wider than the others.
 */
const TEXT_MAX_CH = 20;
const DESCRIPTION_MAX_CH = 32;

function numericColumns(map: ColumnMap): Set<number> {
  return new Set(
    [map.amount, map.debit, map.credit, map.runningBalance].filter(
      (index): index is number => index !== null,
    ),
  );
}

/** One line, an ellipsis at `maxCh`; a cell that can be cut off shows its full text on hover and focus. */
function TextCell({ text, maxCh, strong }: { text: string; maxCh: number; strong: boolean }) {
  const tokens = resolveTokens(useTheme());
  const long = text.length > maxCh;
  const cell = (
    <Typography
      variant="monoSmall"
      component="span"
      data-text-cell=""
      {...(long ? { tabIndex: 0, 'data-long-cell': '' } : {})}
      sx={{
        display: 'block',
        maxWidth: `${maxCh}ch`,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        ...(strong ? { fontWeight: 500 } : {}),
        '&:focus-visible': { outline: `2px solid ${tokens.focusRing}`, outlineOffset: 2 },
      }}
    >
      {text}
    </Typography>
  );
  return long ? (
    <Tooltip title={text} describeChild>
      {cell}
    </Tooltip>
  ) : (
    cell
  );
}

interface RawSampleProps {
  rows: readonly (readonly string[])[];
  /** Rows before the detected header. Omitted when the server sent only a count. */
  preambleSample?: readonly (readonly string[])[];
  /** 1-based file row of `preambleSample[0]`. Defaults to `preambleRows + 1` when the sample is empty. */
  preambleFrom?: number;
  /** Parsed rows above the detected header, including ones the sample does not hold. */
  preambleRows: number;
  columnMap: ColumnMap;
  headerRow: number;
  firstDataRow: number;
  /** Each held row's number opens "Use as header row" / "Use as first data row" (TMMAPP-0136 4b). */
  onPickHeader?: ((fileRow: number) => void) | undefined;
  onPickFirstData?: ((fileRow: number) => void) | undefined;
}

/** The row's number as a button: Enter or a click opens its menu, and a choice sets the field. */
function RowPicker({
  fileRow,
  onPickHeader,
  onPickFirstData,
}: {
  fileRow: number;
  onPickHeader: (fileRow: number) => void;
  onPickFirstData: (fileRow: number) => void;
}) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const menuId = useId();
  const pick = (apply: (fileRow: number) => void) => {
    setAnchor(null);
    apply(fileRow);
  };
  return (
    <>
      <Box
        component="button"
        type="button"
        aria-label={t('imports.rowMenu', { row: fileRow })}
        aria-haspopup="menu"
        aria-expanded={anchor !== null}
        aria-controls={anchor !== null ? menuId : undefined}
        data-row-picker={fileRow}
        onClick={(event: MouseEvent<HTMLElement>) => setAnchor(event.currentTarget)}
        sx={{
          p: 0,
          m: 0,
          border: 0,
          background: 'none',
          color: 'inherit',
          font: 'inherit',
          cursor: 'pointer',
          textDecoration: 'underline dotted',
          textUnderlineOffset: 3,
          '&:focus-visible': { outline: `2px solid ${tokens.focusRing}`, outlineOffset: 2 },
        }}
      >
        <Typography variant="monoSmall" component="span">
          {fileRow}
        </Typography>
      </Box>
      <Menu id={menuId} anchorEl={anchor} open={anchor !== null} onClose={() => setAnchor(null)}>
        <MenuItem onClick={() => pick(onPickHeader)}>{t('imports.useAsHeader')}</MenuItem>
        <MenuItem onClick={() => pick(onPickFirstData)}>{t('imports.useAsFirstData')}</MenuItem>
      </Menu>
    </>
  );
}

type RowKind = 'above' | 'header' | 'first' | 'skipped' | 'data';

const STICKY = { position: 'sticky', top: 0, zIndex: 1 } as const;

/** Cell ink. `&&` beats `MuiTableCell`'s body colour and the head slot (`text2` on `surface2`). */
function cellSx(
  ink: { color: string; backgroundColor?: string; fontWeight?: number } | null,
  layout: Record<string, string | number>,
) {
  if (ink === null) return layout;
  return { ...layout, '&&': ink };
}

function rowKind(fileRow: number, headerRow: number, firstDataRow: number): RowKind {
  if (fileRow < headerRow) return 'above';
  if (fileRow === headerRow) return 'header';
  if (fileRow === firstDataRow) return 'first';
  return fileRow > firstDataRow ? 'data' : 'skipped';
}

function rowLabel(
  kind: RowKind,
): 'imports.rowHeader' | 'imports.rowFirstData' | 'imports.rowData' | 'imports.rowAbove' | null {
  if (kind === 'header') return 'imports.rowHeader';
  if (kind === 'first') return 'imports.rowFirstData';
  if (kind === 'data') return 'imports.rowData';
  if (kind === 'above') return 'imports.rowAbove';
  return null;
}

export function RawSample({
  rows,
  preambleSample = [],
  preambleFrom,
  preambleRows,
  columnMap,
  headerRow,
  firstDataRow,
  onPickHeader,
  onPickFirstData,
}: RawSampleProps) {
  const { t } = useTranslation();
  const picking = onPickHeader !== undefined && onPickFirstData !== undefined;
  const tokens = resolveTokens(useTheme());
  const numeric = numericColumns(columnMap);
  const [aboveOpen, setAboveOpen] = useState(false);
  // A served preamble is numbered from `preambleFrom`. An empty sample keeps the old origin:
  // `sampleRows[0]` is file row `preambleRows + 1`, even when `preambleFrom` equals the header.
  const servedPreamble = preambleSample.length;
  const preambleOrigin = preambleFrom ?? 1;
  const sampleOrigin =
    servedPreamble > 0 ? preambleOrigin + servedPreamble : sampleFileRow(preambleRows, 0);
  const indexed = [
    ...preambleSample.map((row, index) => ({ row, fileRow: preambleOrigin + index })),
    ...rows.map((row, index) => ({ row, fileRow: sampleOrigin + index })),
  ];
  // Every file row above the header. The sample holds only the ones it was sent; the rest stay a count.
  const aboveCount = Math.max(0, headerRow - 1);
  const aboveHeld = indexed.filter(({ fileRow }) => fileRow < headerRow).length;
  const aboveUnsent = servedPreamble === 0 ? aboveCount - aboveHeld : 0;
  const partialPreamble = servedPreamble > 0 && preambleRows > servedPreamble;
  const shown = indexed.filter(({ fileRow }) => aboveOpen || fileRow >= headerRow);
  // A header field that points past the preamble and the sample must not tint some other row.
  const headerOutside = indexed.every(({ fileRow }) => fileRow !== headerRow);
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="subtitle2" component="h2" sx={{ mb: 1 }}>
        {t('imports.sampleRaw')}
      </Typography>
      {/* Above the scrolling box, so it stays at the top (TMMAPP-0139 item 4). */}
      {aboveCount > 0 && (
        <Box
          data-rows-above=""
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 0.5,
            mb: 1,
          }}
        >
          {aboveHeld > 0 && (
            <Button
              size="small"
              aria-expanded={aboveOpen}
              onClick={() => setAboveOpen((open) => !open)}
            >
              {t('imports.rowsAboveHeader', { count: aboveHeld })}
            </Button>
          )}
          {aboveUnsent > 0 && (
            <Typography variant="body2" sx={{ color: tokens.text2 }}>
              {t('imports.rowsAboveNotShown', { count: aboveUnsent })}
            </Typography>
          )}
          {partialPreamble && (
            <Typography variant="body2" sx={{ color: tokens.text2 }}>
              {t('imports.preamblePartial', {
                shown: servedPreamble,
                count: preambleRows,
              })}
            </Typography>
          )}
        </Box>
      )}
      <Box
        tabIndex={0}
        role="region"
        aria-label={t('imports.sampleRaw')}
        data-raw-sample=""
        sx={{
          overflow: 'auto',
          maxHeight: '60vh',
          minWidth: 0,
          border: `1px solid ${tokens.line}`,
          borderRadius: `${tokens.radius.control}px`,
          '&:focus-visible': { outline: `2px solid ${tokens.focusRing}`, outlineOffset: -2 },
        }}
      >
        <Table size="small" aria-label={t('imports.sampleRaw')}>
          <TableBody>
            {shown.map(({ row, fileRow }) => {
              const kind = rowKind(fileRow, headerRow, firstDataRow);
              const label = rowLabel(kind);
              const header = kind === 'header';
              const ink =
                kind === 'header'
                  ? {
                      color: tokens.infoText,
                      backgroundColor: tokens.infoSoft,
                      fontWeight: 500,
                    }
                  : kind === 'first'
                    ? { color: tokens.successText, backgroundColor: tokens.successSoft }
                    : kind === 'above'
                      ? { color: tokens.text2 }
                      : null;
              // The header row stays at the top of the box as it scrolls (its fill is opaque).
              const stick = header ? STICKY : {};
              return (
                <TableRow key={`r-${fileRow}`} data-file-row={fileRow} data-row-kind={kind}>
                  <TableCell variant="head" sx={cellSx(ink, { width: 40, ...stick })}>
                    {picking ? (
                      <RowPicker
                        fileRow={fileRow}
                        onPickHeader={onPickHeader}
                        onPickFirstData={onPickFirstData}
                      />
                    ) : (
                      <Typography variant="monoSmall">{fileRow}</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={cellSx(ink, { whiteSpace: 'nowrap', width: 120, ...stick })}>
                    {label !== null && (
                      <Typography variant="caption" sx={{ fontWeight: header ? 500 : undefined }}>
                        {t(label)}
                      </Typography>
                    )}
                  </TableCell>
                  {row.map((cell, col) => {
                    const number = numeric.has(col);
                    return (
                      <TableCell
                        key={`c-${fileRow}-${col}`}
                        align={number ? 'right' : 'left'}
                        {...(number ? { 'data-numeric-cell': '' } : {})}
                        sx={cellSx(ink, {
                          whiteSpace: 'nowrap',
                          ...(number ? { fontVariantNumeric: 'tabular-nums' } : {}),
                          ...stick,
                        })}
                      >
                        {number ? (
                          <Typography
                            variant="monoSmall"
                            sx={{
                              fontVariantNumeric: 'tabular-nums',
                              ...(header ? { fontWeight: 500 } : {}),
                            }}
                          >
                            {cell}
                          </Typography>
                        ) : (
                          <TextCell
                            text={cell}
                            maxCh={col === columnMap.payee ? DESCRIPTION_MAX_CH : TEXT_MAX_CH}
                            strong={header}
                          />
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
      {headerOutside && (
        <Typography variant="body2" sx={{ color: tokens.text2, mt: 1 }} data-header-outside="">
          {t('imports.headerOutsideSample', { row: headerRow })}
        </Typography>
      )}
    </Box>
  );
}
